import { useState } from "react";
import { Modal } from "react-bootstrap";
import SntButton from "@/cleanup/widgets/forms/SntButton";
import IcomoonIcon from "@icons/IcomoonIcon";
import { translate } from "@/hooks/useLanguage";
import { LanguageKey } from "@/types/generated/LanguageKey";

interface ToolDetailsDialogProps {
  isShow: boolean;
  onClose: () => void;
  toolName: string;
  toolArguments?: string;
  toolResult?: string;
}

/**
 * Dialog showing tool invocation details (request parameters and response)
 */
const ToolDetailsDialog = ({
  isShow,
  onClose,
  toolName,
  toolArguments,
  toolResult,
}: ToolDetailsDialogProps) => {
  const [copiedRequest, setCopiedRequest] = useState(false);
  const [copiedResponse, setCopiedResponse] = useState(false);

  const formatJson = (jsonString?: string): string => {
    if (!jsonString) return "N/A";
    try {
      const parsed = JSON.parse(jsonString);
      return JSON.stringify(parsed, null, 2);
    } catch {
      return jsonString;
    }
  };

  const copyToClipboard = async (text: string, setCopied: (v: boolean) => void) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy to clipboard:", err);
    }
  };

  const formattedRequest = formatJson(toolArguments);
  const formattedResponse = formatJson(toolResult);

  return (
    <Modal
      show={isShow}
      onHide={onClose}
      size="lg"
      dialogClassName="modal-dialog-scrollable"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <span className="font-monospace">{toolName}</span>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="snt-scroll">
        <div className="mb-4">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h6 className="text-muted mb-0">{translate(LanguageKey.request_parameters_key)}</h6>
            {toolArguments && (
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={() => copyToClipboard(formattedRequest, setCopiedRequest)}
                title={translate(LanguageKey.copy_to_clipboard_key)}
              >
                <IcomoonIcon icon={copiedRequest ? "checkmark" : "copy"} size={14} />
                <span className="ms-1">{copiedRequest ? translate(LanguageKey.copied_to_clipboard_key) : translate(LanguageKey.copy_key)}</span>
              </button>
            )}
          </div>
          <pre
            className="bg-light p-3 rounded small"
            style={{
              maxHeight: "300px",
              overflow: "auto",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {formattedRequest}
          </pre>
        </div>

        <div>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h6 className="text-muted mb-0">{translate(LanguageKey.response_key)}</h6>
            {toolResult && (
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={() => copyToClipboard(formattedResponse, setCopiedResponse)}
                title={translate(LanguageKey.copy_to_clipboard_key)}
              >
                <IcomoonIcon icon={copiedResponse ? "checkmark" : "copy"} size={14} />
                <span className="ms-1">{copiedResponse ? translate(LanguageKey.copied_to_clipboard_key) : translate(LanguageKey.copy_key)}</span>
              </button>
            )}
          </div>
          <pre
            className="bg-light p-3 rounded small"
            style={{
              maxHeight: "400px",
              overflow: "auto",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {formattedResponse}
          </pre>
        </div>
      </Modal.Body>

      <Modal.Footer className="d-flex justify-content-start">
        <SntButton variant="outline-sensolus" onClick={onClose} text={translate(LanguageKey.close_key)} trackingKey="close_key" />
      </Modal.Footer>
    </Modal>
  );
};

export default ToolDetailsDialog;
